import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, animate } from 'framer-motion';

// --- ANIMATION VARIANTS for Framer Motion ---
const sectionVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.6, -0.05, 0.01, 0.99],
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

// --- AnimatedSection Wrapper ---
const AnimatedSection = ({ children, id, className = '' }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });

    return (
        <motion.section
            id={id}
            ref={ref}
            className={`py-24 md:py-32 overflow-hidden ${className}`}
            variants={sectionVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
        >
            {children}
        </motion.section>
    );
};

const AnimatedText = ({ text, className, variants, ...props }) => {
    const words = text.split(" ");
    return (
        <motion.p className={className} variants={variants} {...props}>
            {words.map((word, i) => (
                <motion.span key={i} className="inline-block mr-[0.25em]" variants={itemVariants}>
                    {word}
                </motion.span>
            ))}
        </motion.p>
    );
};

// --- ABOUT SECTION & STATS ---
const AnimatedStat = ({ value, label, suffix = '' }) => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (isInView) {
            animate(0, value, {
                duration: 2,
                ease: "easeOut",
                onUpdate: (latest) => {
                    setCount(Math.round(latest));
                }
            });
        }
    }, [isInView, value]);

    return (
        <motion.div ref={ref} className="text-center" variants={itemVariants}>
            <p className="text-4xl md:text-5xl font-bold text-cyan-500 dark:text-cyan-400">{count}{suffix}</p>
            <p className="text-slate-600 dark:text-gray-400 mt-2">{label}</p>
        </motion.div>
    );
};

const AboutSection = () => (
    <AnimatedSection id="about" className="bg-white dark:bg-black text-slate-900 dark:text-white">
        <div className="container mx-auto px-6">
            <motion.div className="text-center max-w-4xl mx-auto" variants={sectionVariants}>
                <AnimatedText text="We are a new breed of digital architects." className="text-4xl md:text-6xl font-bold mb-6" variants={sectionVariants}/>
                <motion.p variants={itemVariants} className="text-xl md:text-2xl text-slate-600 dark:text-gray-400 leading-relaxed mb-16">
                    Smart Solutions isn't just a consultancy; we are your strategic partner in innovation. We fuse creative design with robust engineering to build digital experiences that are powerful, scalable, and built for the future.
                </motion.p>
            </motion.div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
                <AnimatedStat value={100} label="Projects Completed" suffix="+" />
                <AnimatedStat value={98} label="Client Satisfaction" suffix="%" />
                <AnimatedStat value={10} label="Expert Team Members" suffix="+" />
                <AnimatedStat value={5} label="Years of Innovation" suffix="+" />
            </div>
        </div>
    </AnimatedSection>
);

export default AboutSection;
